import { useQuery } from '@tanstack/react-query';

import recognitionService from './recognitionService';

import type { ReceivedRecognitions } from '@/types/recognition';

import { EmployeePortalQueryKey } from '@/constants/QueryKeys';
import { shuffle } from '@/utils/shuffle';

/**
 * Deals the server's ten newest in the random order the portal shows them in. Declared once, at
 * module level, so React Query sees the same `select` on every render and runs it only when an
 * answer arrives: the order holds still while the reader browses the carousel, and structural
 * sharing keeps a refetch that brings back the same ten from dealing them again.
 */
const shuffleRecognitions = ({
    recognitions,
    feedPath,
}: ReceivedRecognitions): ReceivedRecognitions => ({
    feedPath,
    recognitions: shuffle(recognitions),
});

/**
 * The recognitions an employee received, newest ten, in a random order: the signed-in employee's
 * own, or -- for a manager reading an employee's portal -- that employee's.
 */
export const useGetReceivedRecognitions = (employeeId?: string) =>
    useQuery({
        queryKey: [EmployeePortalQueryKey.RECOGNITIONS, employeeId],
        queryFn: () => recognitionService.getReceivedRecognitions(employeeId),
        select: shuffleRecognitions,
    });
